'use client';

// import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type SpeechPayload = {
  city: string;
  condition: string;
  temp: number | null | undefined;
  feelsLike: number | null | undefined;
  windSpeed: number | null | undefined; // м/с
  windDeg: number;
  humidity: number | null | undefined;
  tempMin: number | null | undefined;
  tempMax: number | null | undefined;
} | null;

export default function WeatherAssistantClient({
  adviceList,
  speechPayload,
}: {
  adviceList: string[];
  speechPayload: SpeechPayload;
}) {
  // const reduceMotion = useReducedMotion();

  const [currentAdviceIndex, setCurrentAdviceIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [isSpeechReady, setIsSpeechReady] = useState(false);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setIsSpeechReady(typeof window !== 'undefined' && 'speechSynthesis' in window);
  }, []);

  useEffect(() => {
    setCurrentAdviceIndex((i) => (adviceList.length ? i % adviceList.length : 0));
    setIsVisible(true);
  }, [adviceList]);

  useEffect(() => {
    if (!adviceList.length) return;

    const tick = () => {
      // if (reduceMotion) {
      //   setCurrentAdviceIndex((prev) => (prev + 1) % adviceList.length);
      //   return;
      // }
      setIsVisible(false);
      timeoutRef.current = setTimeout(() => {
        setCurrentAdviceIndex((prev) => (prev + 1) % adviceList.length);
        setIsVisible(true);
      }, 250);
    };

    intervalRef.current = setInterval(tick, 20_000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [adviceList]);

  const windDirText = useMemo(() => {
    if (!speechPayload) return '';
    const dirs = [
      'северный',
      'северо-восточный',
      'восточный',
      'юго-восточный',
      'южный',
      'юго-западный',
      'западный',
      'северо-западный',
    ];
    const ix = Math.round((speechPayload.windDeg ?? 0) / 45) % 8;
    return dirs[ix];
  }, [speechPayload]);

  const handleSpeakForecast = useCallback(() => {
    if (!isSpeechReady || !speechPayload) return;

    const val = (x: number | null | undefined) =>
      x === null || x === undefined || Number.isNaN(x) ? null : x;

    const temp = val(speechPayload.temp);
    const feels = val(speechPayload.feelsLike);
    const windMs = val(speechPayload.windSpeed);
    const humidity = val(speechPayload.humidity);
    const tMin = val(speechPayload.tempMin);
    const tMax = val(speechPayload.tempMax);

    const textToSpeak = `
      Сейчас в городе ${speechPayload.city} ${speechPayload.condition}.
      Температура ${temp !== null ? Math.round(temp) : 'нет данных'} градусов, ощущается как ${feels !== null ? Math.round(feels) : 'нет данных'}.
      Ветер ${windMs !== null ? Math.round(windMs * 3.6) : 'нет данных'} километров в час, направление ${windDirText}.
      Влажность воздуха ${humidity !== null ? humidity : 'нет данных'} процентов.
      Максимальная температура сегодня ${tMax !== null ? Math.round(tMax) : 'нет данных'}, минимальная ${tMin !== null ? Math.round(tMin) : 'нет данных'}.
      Хорошего дня!
    `;

    window.speechSynthesis.cancel(); // чтобы речь не наслаивалась

    const utterance = new SpeechSynthesisUtterance(textToSpeak);
    utterance.lang = 'ru-RU';
    window.speechSynthesis.speak(utterance);
  }, [isSpeechReady, speechPayload, windDirText]);

  const currentText = adviceList.length ? adviceList[currentAdviceIndex] : 'Советы недоступны.';

  return (
    <div className="flex flex-col min-h-[350px] p-4 md:p-6 bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-lg shadow-lg">
      <h2 className="text-2xl font-semibold mb-3 text-main">Ваш AI-помощник</h2>

      <div className="min-h-[60px] text-2xl font-bold" aria-live="polite" aria-atomic="true">
        {/* <AnimatePresence mode="wait">
          {isVisible && (
            <motion.p
            key={currentAdviceIndex}
            className="mb-2 text-sky-100"
            initial={reduceMotion ? undefined : { opacity: 0, scale: 0.98 }}
            animate={reduceMotion ? undefined : { opacity: 1, scale: 1 }}
            exit={reduceMotion ? undefined : { opacity: 0, scale: 0.98 }}
            transition={reduceMotion ? undefined : { duration: 0.25 }}
          >
            {currentText}
          </motion.p>
          )}
        </AnimatePresence> */}
         <p
          key={currentAdviceIndex}
          className={`mb-2 text-sky-100 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        >
          {currentText}
        </p>
      </div>

      <div className="mt-auto">
        <button
          onClick={handleSpeakForecast}
          disabled={!isSpeechReady || !speechPayload}
          className="mt-4 bg-teal-500 hover:bg-teal-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Озвучить текущий прогноз погоды"
        >
          Озвучить прогноз
        </button>
      </div>
    </div>
  );
}
